import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Button,
  InputAdornment,
  SelectChangeEvent,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { NOTE_TYPES, noteTypeColor, noteTypeLabel } from '../../../constants/noteTypes';

export interface FilterState {
  searchQuery: string;
  selectedTypes: string[];
  dateRange: string;
  customStartDate?: Date | null;
  customEndDate?: Date | null;
}

interface NotesFilterBarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  totalCount: number;
  filteredCount: number;
}

const DATE_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom Range' },
];

export const NotesFilterBar: React.FC<NotesFilterBarProps> = ({
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
}) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      searchQuery: event.target.value,
    });
  };

  const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      selectedTypes: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleDateRangeChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      dateRange: event.target.value,
    });
  };

  const handleRemoveType = (typeToRemove: string) => {
    onFiltersChange({
      ...filters,
      selectedTypes: filters.selectedTypes.filter(t => t !== typeToRemove),
    });
  };

  const handleClearAll = () => {
    onFiltersChange({
      searchQuery: '',
      selectedTypes: [],
      dateRange: 'all',
      customStartDate: null,
      customEndDate: null,
    });
  };

  const hasActiveFilters =
    filters.searchQuery !== '' ||
    filters.selectedTypes.length > 0 ||
    filters.dateRange !== 'all';

  const getTypeColor = noteTypeColor;
  const getTypeLabel = noteTypeLabel;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Filter Controls */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search notes..."
          value={filters.searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />

        {/* Type Filter */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="type-filter-label">Type</InputLabel>
          <Select
            labelId="type-filter-label"
            multiple
            value={filters.selectedTypes}
            onChange={handleTypeChange}
            input={<OutlinedInput label="Type" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={getTypeLabel(value)}
                    size="small"
                    sx={{
                      bgcolor: getTypeColor(value),
                      color: 'white',
                      height: 20,
                    }}
                  />
                ))}
              </Box>
            )}
          >
            {NOTE_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Checkbox checked={filters.selectedTypes.indexOf(type.value) > -1} />
                <ListItemText primary={type.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range Filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="date-range-label">Date Range</InputLabel>
          <Select
            labelId="date-range-label"
            value={filters.dateRange}
            onChange={handleDateRangeChange}
            label="Date Range"
          >
            {DATE_RANGES.map((range) => (
              <MenuItem key={range.value} value={range.value}>
                {range.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
            <FilterListIcon fontSize="small" color="action" />
            <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              Filters:
            </Box>
          </Box>

          {filters.selectedTypes.map((type) => (
            <Chip
              key={type}
              label={getTypeLabel(type)}
              onDelete={() => handleRemoveType(type)}
              size="small"
              sx={{
                bgcolor: getTypeColor(type),
                color: 'white',
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                },
              }}
            />
          ))}

          {filters.searchQuery && (
            <Chip
              label={`Search: "${filters.searchQuery}"`}
              onDelete={() => onFiltersChange({ ...filters, searchQuery: '' })}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}

          {filters.dateRange !== 'all' && (
            <Chip
              label={DATE_RANGES.find(r => r.value === filters.dateRange)?.label}
              onDelete={() => onFiltersChange({ ...filters, dateRange: 'all' })}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}

          <Button
            size="small"
            onClick={handleClearAll}
            startIcon={<ClearIcon />}
            sx={{ ml: 'auto' }}
          >
            Clear All
          </Button>
        </Box>
      )}

      {/* Results Count */}
      <Box sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
        Showing {filteredCount} of {totalCount} notes
      </Box>
    </Box>
  );
};
